/* eslint-disable react-refresh/only-export-components -- SLOT_TITLE_MAX and shared constants are co-exported alongside the component for use in sibling pages */
import React, { useState, useCallback, useRef, useEffect, useMemo, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '../components/LocaleLink';
import { RotateCcw, Settings, Share2, Pencil, Check, Code2, X, Copy, CheckCheck, Bookmark as BookmarkIcon, QrCode, Scale, List as ListIcon, Layers, ImageDown, ListPlus, Repeat2, ChevronRight, ChevronDown } from 'lucide-react';
import SpinWheel, { SpinWheelRef } from '../components/SpinWheel';
import EntriesPanel from '../components/EntriesPanel';
import MobileEntriesPanel from '../components/MobileEntriesPanel';
import WinnerBanner from '../components/WinnerBanner';
import OptionsModal, { WheelOptions, DEFAULT_OPTIONS, FONT_STACK_MAP } from '../components/OptionsModal';
import { readSavedWheels, appendSlotHistory } from '../utils/savedWheels';

import GallerySection, { WheelTemplate, TEMPLATES } from '../components/GallerySection';
import FaqSection from '../components/FaqSection';
import { FAQS, homeFaqs } from '../data/faqs';
import WinnerModal, { Confetti } from '../components/WinnerModal';
import TrustBadges from '../components/trust/TrustBadges';
import TechnicalSpecs from '../components/trust/TechnicalSpecs';
import PurposeBuiltTools from '../components/PurposeBuiltTools';
import LinkedWheelsSpotlight from '../components/LinkedWheelsSpotlight';
import UseCasesSection from '../components/UseCasesSection';
import { useSEO } from '../utils/useSEO';
import { useLang, LANGUAGES } from '../utils/i18n';
import type { WheelEntry } from '../types/wheel';
import { UNDO_TIMEOUT_MS } from '../utils/constants';
import { clampWeight, isActive, isUniform, DEFAULT_WEIGHT } from '../utils/weights';
import { randomInt } from '../utils/random';
import { loadResults, saveResults, type ResultEntry } from '../utils/results';
import { downloadWheelImage } from '../utils/wheelImage';
import { useUndoHistory } from '../utils/useUndoHistory';
import { useAnnouncer } from '../hooks/useAnnouncer';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useMediaQuery } from '../utils/useMediaQuery';
import UndoToast from '../components/UndoToast';
import ResumeWheelBanner from '../components/ResumeWheelBanner';
import { MAX_ENTRIES, MAX_DRAWN_SEGMENTS, MAX_SHARE_URL_BYTES, clampEntries, isSampled } from '../utils/limits';
import { buildSharedWheelUrl, decodeShare, encodeShare, isValidImageDataUrl, MAX_WHEEL_TITLE_LENGTH, normalizeEntries, normalizeTitle, shareUrlByteLength } from '../utils/wheelData';
import type { LandingPreset } from '../data/landingPages';
import { PALETTE_NAMES } from '../utils/palettes';
import MainToolLocalizedContent from '../components/MainToolLocalizedContent';
import { hasMainToolTranslations, resolveMainToolContent } from '../data/mainToolI18n';
import { useMainToolStructuredData } from '../utils/useMainToolStructuredData';

// ── Lazy-loaded components — these split into their own chunks ─────────────────
// Only components not statically imported anywhere else can actually split out.
// The bundler warned about the others and kept them in the main chunk anyway.
const SavedWheelsModal      = React.lazy(() => import('../components/SavedWheelsModal'));
const QRModal               = React.lazy(() => import('../components/QRModal'));
import HeroWheelAnimation from '../components/HeroWheelAnimation';
const OnboardingTour        = React.lazy(() => import('../components/OnboardingTour'));


/**
 * The names a first-time visitor sees. They are only a demo — the first thing
 * most people do is replace them — but they are also the first impression.
 *
 * Deliberately drawn from different regions rather than one: the previous list
 * (Alice, Bob, Charlie …) was entirely Anglo-American, which reads as foreign
 * to most of the audience given the UI ships in 31 languages.
 *
 * A mixed set rather than per-locale detection, for two reasons. Getting names
 * right for 31 locales is not something that can be done reliably from outside
 * those cultures, and a wrong guess is worse than no guess. And a locale-matched
 * list only ever shows one culture — this way every visitor sees a wheel that
 * includes somewhere like theirs. Per-locale lists remain possible later:
 * `defaultNames` already overrides this, so a lookup keyed on the active
 * language would slot in without touching anything else.
 *
 * Chosen to stay legible on a ten-slice wheel: given names only, no surnames,
 * all short, and in Latin script so they render in every bundled font.
 */
export const DEFAULT_NAMES = ['Aisha','Chen','Diego','Fatima','Hana','Kwame','Lucas','Meera','Omar','Sofia'];

interface WinnerInfo {
  name: string;
  index: number;
  imageDataUrl?: string;
}

export interface HomePageProps {
  defaultNames?: string[];
  /** Optional initial weights parallel to `defaultNames`; missing values use the normal weight of 1. */
  defaultWeights?: number[];
  defaultTitle?: string;
  storageKeyPrefix?: string;
  disableSEO?: boolean;
  /**
   * Render the homepage's marketing sections (gallery, use cases, FAQ,
   * cross-links). Preset landing pages set this to false and supply their own
   * copy — otherwise the same blocks appear on four URLs and compete with each
   * other in search.
   */
  showLandingSections?: boolean;
  /** One-click entry-list presets shown above the wheel (/number-wheel, /random-letter-wheel). */
  presets?: LandingPreset[];
  /** Show the Min/Max numeric range builder above the wheel (/number-wheel). */
  rangeBuilder?: boolean;
  /**
   * Round-robin team assignment (/random-team-generator). Each spin sends its
   * winner to the next team and takes that name off the wheel.
   */
  teamMode?: boolean;
  /**
   * Heading level for the editable wheel title.
   *
   * It is an <h1> where the widget IS the page (the homepage and the three
   * preset wheels). Pages that print their own <h1> above the widget pass 'h2'
   * — two <h1>s would leave the document with no single main heading, and the
   * second one is a user-editable label rather than a description of the page.
   */
  titleAs?: 'h1' | 'h2';
  /**
   * Called by NameListPage when a visitor clicks a name in the list below the
   * wheel. Receives the name string; the wheel appends it to its entries.
   * Deliberately a callback prop rather than a ref so it stays within React's
   * data flow and is type-safe without useImperativeHandle.
   */
  onAddName?: (name: string) => void;
  /**
   * Register a function that external code (NameListPage) can call to inject
   * a single name into the wheel. NameListPage passes a setter here; the
   * wheel calls it with its own `handleAddName` once mounted.
   */
  registerAddName?: (fn: (name: string) => void) => void;
  /**
   * The full editor is also rendered inside the dedicated iframe app. Embed
   * mode keeps every wheel control while suppressing recursive embedding and
   * the first-visit tour, which would otherwise cover a small host frame.
   */
  embedMode?: boolean;
  /** Let a returning iframe visitor resume local edits instead of reapplying its URL seed. */
  preferStoredState?: boolean;
  /** Validated appearance and behavior defaults supplied by an embed URL. */
  initialOptions?: Partial<WheelOptions>;
  /** Host-page bridge used by the full iframe app. */
  onEmbeddedSpinStart?: () => void;
  /** Host-page bridge used by the full iframe app. */
  onEmbeddedWinner?: (winner: WinnerInfo) => void;
}

/**
 * Cap on generated ranges. A wheel built from "1 to 100000" would freeze the
 * canvas for seconds and be unreadable; 200 already renders as hairlines.
 */
/**
 * The range builder is the only way to reach a five-figure list in one action,
 * and generating ticket numbers 1–50,000 is exactly what people want it for.
 * It was capped at 200 back when every entry became a drawn slice; now that
 * rendering is bounded independently, the entry cap is the only real limit.
 */
const MAX_RANGE_SIZE = MAX_ENTRIES;

/**
 * Homepage proof points.
 *
 * Deliberately not usage statistics. Each is verifiable from the source — the
 * template count, the language count, the entry cap, and local-by-default
 * storage — so none can quietly become false. Invented figures
 * ("500,000+ spins", "4.9 stars") were removed from this file once already; a
 * number nobody can check is worth less than a smaller one that is true,
 * especially on a site schools are evaluating.
 *
 * The privacy point is stated as a capability rather than an absence. "0
 * entries ever sent to a server" is the same fact, but it leads with a zero and
 * reads as a disclaimer — which undersells the one thing here that Picker Wheel
 * and Wheel of Names cannot match.
 */
/**
 * Homepage "how it works" steps.
 *
 * Kept separate from HOWTO_STEPS in src/data/howto.ts on purpose. That array
 * drives /how-to and its HowTo structured data with six detailed steps; this is
 * a four-step summary in its own words. Sharing the array would put identical
 * copy on two indexed URLs, which makes them compete rather than reinforce.
 */
const HOME_STEPS = [
  {
    icon: ListPlus,
    title: 'Add your entries',
    body: 'Type names or paste a whole list from a spreadsheet.',
  },
  {
    icon: RotateCcw,
    title: 'Spin the wheel',
    body: 'Click Spin — the winner is chosen before it starts turning.',
  },
  {
    icon: Repeat2,
    title: 'Keep going or remove',
    body: 'Spin again, or auto-remove winners to work through a list.',
  },
  {
    icon: Share2,
    title: 'Save or share',
    body: 'Save locally, or share a link containing supported wheel data.',
  },
];

const PROOF_POINTS = [
  { value: 'Local', label: 'by default — sharing puts entries in the link' },
  { value: String(TEMPLATES.length), label: 'ready-made wheel templates' },
  { value: String(LANGUAGES.length), label: 'interface languages' },
  { value: '50k', label: 'entries supported per wheel' },
];

// ── INP: Stable spin-button style objects ────────────────────────────────────
// Defined at module scope so they are referentially stable across renders.
// An inline `style={{ background: isSpinning ? ... : ... }}` creates a new
// object on every render — during a spin that is 60 objects/sec being allocated
// and GC'd, each one triggering React's prop-diff path. Stable references skip
// that diff entirely.
const SPIN_BTN_STYLE_IDLE: React.CSSProperties = {
  background: 'linear-gradient(135deg,#7c3aed 0%,#5b21b6 100%)',
  boxShadow: '0 6px 20px rgba(124,58,237,0.4)',
};
const SPIN_BTN_STYLE_BUSY: React.CSSProperties = {
  background: '#cbd5e1',
  boxShadow: 'none',
};

// ── INP: scheduler.yield() shim ─────────────────────────────────────────────
// scheduler.yield() hands control back to the browser so it can paint the
// current frame (e.g. button going disabled/grey) before we schedule heavy
// work. Falls back to a zero-timeout when the API is not available.
// Unlike setTimeout(fn, 0), scheduler.yield() is Scheduler-priority-aware and
// does not yield to timers/IO — only to higher-priority tasks and rendering.
async function yieldToBrowser(): Promise<void> {
  const scheduling = (globalThis as typeof globalThis & {
    scheduler?: { yield?: () => Promise<void> };
  }).scheduler;
  if (typeof scheduling?.yield === 'function') {
    await scheduling.yield();
  } else {
    await new Promise<void>(resolve => setTimeout(resolve, 0));
  }
}
const MAX_TEAMS = 10;

function formatTime(d: Date) {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Share encoding v2 (backward compatible) ──────────────────────────────────

// ── localStorage helpers ──────────────────────────────────────────────────────

function normalizeOptions(value: unknown): WheelOptions {
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const normalized: WheelOptions = {
    autoRemove: typeof raw.autoRemove === 'boolean' ? raw.autoRemove : DEFAULT_OPTIONS.autoRemove,
    soundEnabled: typeof raw.soundEnabled === 'boolean' ? raw.soundEnabled : DEFAULT_OPTIONS.soundEnabled,
    spinDuration: raw.spinDuration === 'slow' || raw.spinDuration === 'fast' || raw.spinDuration === 'normal'
      ? raw.spinDuration : DEFAULT_OPTIONS.spinDuration,
    showConfetti: typeof raw.showConfetti === 'boolean' ? raw.showConfetti : DEFAULT_OPTIONS.showConfetti,
    winnerDisplay: raw.winnerDisplay === 'banner' || raw.winnerDisplay === 'modal'
      ? raw.winnerDisplay : DEFAULT_OPTIONS.winnerDisplay,
    fontFamily: raw.fontFamily === 'georgia' || raw.fontFamily === 'courier' || raw.fontFamily === 'impact' || raw.fontFamily === 'trebuchet' || raw.fontFamily === 'inter'
      ? raw.fontFamily : DEFAULT_OPTIONS.fontFamily,
    fontWeight: raw.fontWeight === 'normal' || raw.fontWeight === 'bold'
      ? raw.fontWeight : DEFAULT_OPTIONS.fontWeight,
    fontSize: raw.fontSize === 'small' || raw.fontSize === 'medium' || raw.fontSize === 'large' || raw.fontSize === 'auto'
      ? raw.fontSize : DEFAULT_OPTIONS.fontSize,
    weightedSegments: typeof raw.weightedSegments === 'boolean' ? raw.weightedSegments : DEFAULT_OPTIONS.weightedSegments,
    palette: typeof raw.palette === 'string' && PALETTE_NAMES.includes(raw.palette as WheelOptions['palette'])
      ? raw.palette as WheelOptions['palette'] : DEFAULT_OPTIONS.palette,
    rimStyle: raw.rimStyle === 'bold' || raw.rimStyle === 'none' || raw.rimStyle === 'classic'
      ? raw.rimStyle : DEFAULT_OPTIONS.rimStyle,
  };
  if (isValidImageDataUrl(raw.centerImageDataUrl)) {
    normalized.centerImageDataUrl = raw.centerImageDataUrl;
  }
  return normalized;
}

function loadFromStorage(prefix = 'npw'): { entries?: WheelEntry[]; title?: string; options?: unknown } {
  try {
    const rawNames = localStorage.getItem(`${prefix}_names`);
    const rawTitle = localStorage.getItem(`${prefix}_title`);
    const rawOptions = localStorage.getItem(`${prefix}_options`);
    const entries = rawNames ? normalizeEntries(JSON.parse(rawNames)) ?? undefined : undefined;
    const title = rawTitle ? normalizeTitle(rawTitle) : undefined;
    const options: unknown = rawOptions ? JSON.parse(rawOptions) : undefined;
    return { entries, title, options };
  } catch { return {}; }
}

function saveToStorage(entries: WheelEntry[], title: string, options: WheelOptions, prefix = 'npw') {
  try {
    localStorage.setItem(`${prefix}_names`, JSON.stringify(entries));
    localStorage.setItem(`${prefix}_title`, title);
    localStorage.setItem(`${prefix}_options`, JSON.stringify(options));
  } catch { /* storage full or blocked */ }
}

// ── Embed Modal ───────────────────────────────────────────────────────────────

interface EmbedModalProps {
  entries: WheelEntry[];
  title: string;
  options: WheelOptions;
  onClose: () => void;
  onShowQR: () => void;
}

const EmbedModal: React.FC<EmbedModalProps> = ({ entries, title, options, onClose, onShowQR }) => {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);
  const modalRef = useFocusTrap(true);
  const encoded = encodeShare(title, entries, options.centerImageDataUrl);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://thenamewheel.com';
  const embedTitle = t('wheel.embedPreview');
  const iframeSrc = buildSharedWheelUrl(origin, '/embed/app/', encoded, {
    palette: options.palette,
    rim: options.rimStyle,
    font: options.fontFamily,
    fontWeight: options.fontWeight,
    fontSize: options.fontSize,
    weighted: options.weightedSegments ? '1' : '0',
    autoRemove: options.autoRemove ? '1' : '0',
    sound: options.soundEnabled ? '1' : '0',
    duration: options.spinDuration,
    confetti: options.showConfetti ? '1' : '0',
    winner: options.winnerDisplay,
  });
  const snippet = `<iframe
  src="${iframeSrc}"
  title="${embedTitle.replace(/"/g, '&quot;')}"
  width="100%"
  height="900"
  loading="lazy"
  style="width:100%;max-width:1200px;border:0;border-radius:16px;"
  sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
  allow="autoplay; fullscreen; clipboard-write"
  referrerpolicy="strict-origin-when-cross-origin"
  allowfullscreen>
</iframe>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard denied */ }
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="embed-modal-title"
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-winner-pop"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 id="embed-modal-title" className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
            <Code2 size={18} className="text-violet-500" /> {t('wheel.embed')}
          </h2>
          <button onClick={onClose} aria-label={t('wheel.close')} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {t('wheel.embedDesc')}
          </p>
          {/* Live preview */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('wheel.preview')}</p>
            <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 flex items-center justify-center" style={{ height: 300 }}>
              <iframe
                src={iframeSrc}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; fullscreen; clipboard-write"
                allowFullScreen
                title={embedTitle}
                className="w-full h-full rounded-lg"
              />
            </div>
          </div>

          {/* Snippet */}
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('wheel.embedCode')}</p>
            <pre dir="ltr" className="text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-xs font-mono text-gray-700 dark:text-gray-200 overflow-x-auto whitespace-pre">
              {snippet}
            </pre>
          </div>

          <button
            onClick={handleCopy}
            className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${
              copied ? 'bg-green-500 text-white' : 'bg-violet-500 hover:bg-violet-700 text-white'
            }`}
          >
            {copied ? <CheckCheck size={16} /> : <Copy size={16} />}
            {copied ? t('wheel.shared') : t('wheel.copyCode')}
          </button>

          <button
            onClick={onShowQR}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <QrCode size={16} />
            {t('wheel.shareQr')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── HomePage ──────────────────────────────────────────────────────────────────

const HomePage: React.FC<HomePageProps> = ({
  defaultNames, defaultWeights, defaultTitle, storageKeyPrefix = 'npw', disableSEO = false,
  showLandingSections = true, presets, rangeBuilder = false, teamMode = false,
  titleAs = 'h2', registerAddName, embedMode = false,
  preferStoredState = false, initialOptions, onEmbeddedSpinStart, onEmbeddedWinner,
}) => {
  const TitleTag = titleAs;
  const location = useLocation();
  const { announce } = useAnnouncer();
  const { lang, t, has, rememberLang } = useLang();
  const localizedHome = resolveMainToolContent('home', t);
  const showLocalizedHome = lang !== 'en' && hasMainToolTranslations('home', has);
  useMainToolStructuredData('home', showLandingSections && !disableSEO && showLocalizedHome);
  const content = showLocalizedHome
    ? {
        h1: localizedHome.h1,
        body: [localizedHome.intro],
      }
    : {
        // <!-- SEO: the homepage has one descriptive H1; editable wheel titles stay H2. -->
        h1: 'Random Name Wheel Spinner',
        body: [
          'Add names, spin the wheel, and pick a random winner instantly.',
          'Your wheel is stored on this device by default. Sharing is optional and places the entries inside the link.',
        ],
      };

  useSEO({
    path: '/',
    breadcrumbs: [{
      name: showLocalizedHome ? t('nav.home') : 'Home',
      path: '/',
    }],
    disabled: disableSEO,
  });

  // Inject FAQPage JSON-LD for the homepage subset. The prerender pipeline
  // already bakes this into the static HTML; this mirrors it for client-side
  // navigations so the structured data is present regardless of entry point.
  useEffect(() => {
    if (disableSEO || lang !== 'en') return;
    const FAQ_JSONLD_ID = 'faq-jsonld';
    document.getElementById(FAQ_JSONLD_ID)?.remove();
    const pageFaqs = homeFaqs();
    const doc = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: pageFaqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = FAQ_JSONLD_ID;
    script.textContent = JSON.stringify(doc).replace(/<\//g, '<\\/');
    document.head.appendChild(script);
    return () => { document.getElementById(FAQ_JSONLD_ID)?.remove(); };
  }, [disableSEO, lang]);

  const [entries, setEntries] = useState<WheelEntry[]>(
    () => (defaultNames ?? DEFAULT_NAMES).map((name, index) => ({
      name,
      weight: clampWeight(defaultWeights?.[index] ?? DEFAULT_WEIGHT),
    }))
  );
  const initialWheelTitle = defaultTitle ?? (lang === 'en' ? 'My Wheel' : 'TheNameWheel');
  const [title, setTitle] = useState(() => initialWheelTitle);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(() => initialWheelTitle);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<WinnerInfo | null>(null);
  const [bannerWinner, setBannerWinner] = useState<WinnerInfo | null>(null);
  const bannerKeyRef = useRef(0);
  // Restored from storage, not empty. A spin history that vanishes on tab close
  // is useless to the two audiences most likely to need it — a teacher recording
  // who was called on, and a raffle host who has to show their working.
  const [results, setResults] = useState<ResultEntry[]>([]);
  // Merge over defaults: options persisted before a new field existed would otherwise
  // leave that field undefined for every returning user.
  const [options, setOptions] = useState<WheelOptions>(DEFAULT_OPTIONS);
  const hasStoredEntriesRef = useRef(false);
  const [showOptions, setShowOptions] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [imageSaved, setImageSaved] = useState(false);
  // Ten-deep, so a burst of removals is recoverable rather than just the last.
  const undoHistory = useUndoHistory<WheelEntry[]>(10);
  const [showSaved, setShowSaved] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [showQR, setShowQR] = useState(false);
  // Entries sheet, below the lg breakpoint only. Closed on load: the wheel is
  // what someone came for, and the default list is usually fine for a first spin.
  const [sheetOpen, setSheetOpen] = useState(false);
  const isDesktopViewport = useMediaQuery(1024);
  // Mobile entries panel: controlled tab so the parent can flip to Results after a spin.
  const [mobileTab, setMobileTab] = useState<'entries' | 'results'>('entries');

  /**
   * Which saved slot is currently loaded, if any.
   *
   * Spins are attached to that slot as they happen, so the record builds up
   * while the class runs instead of only when someone remembers to press Save.
   * A ref, not state: handleSpinComplete is memoised, and re-creating it on
   * every load would also re-create SpinWheel's imperative spin handle.
   */
  const activeSlotRef = useRef<string | null>(null);

  // Browser storage is loaded only after hydration so returning-user state
  // cannot make the first client render disagree with the pre-rendered HTML.
  useEffect(() => {
    const saved = loadFromStorage(storageKeyPrefix);
    hasStoredEntriesRef.current = saved.entries !== undefined;
    if (saved.entries) setEntries(saved.entries);
    if (saved.title) {
      setTitle(saved.title);
      setTitleDraft(saved.title);
    }
    if (saved.options !== undefined) {
      setOptions(normalizeOptions(saved.options));
    } else if (initialOptions) {
      // Applied after hydration: production serves one prerendered embed shell
      // for every query string, so reading URL options during initial render
      // would make the client tree disagree with that HTML.
      setOptions({ ...DEFAULT_OPTIONS, ...initialOptions });
    }
    setResults(loadResults(storageKeyPrefix));
  }, [storageKeyPrefix, initialOptions]);

  // Lock the page behind the sheet. Without this, a scroll gesture that reaches
  // the end of the entry list keeps going and scrolls the page underneath —
  // the sheet appears to come unstuck from the screen.
  useEffect(() => {
    if (!sheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [sheetOpen]);

  // Escape closes it, matching every other dismissible surface on the site.
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheetOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheetOpen]);
  const wheelRef = useRef<SpinWheelRef>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const optionsBtnRef = useRef<HTMLButtonElement>(null);
  const spinBtnRef = useRef<HTMLButtonElement>(null);
  const sheetOpenerRef = useRef<HTMLButtonElement>(null);
  const entriesPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    entriesPanelRef.current?.toggleAttribute('inert', !isDesktopViewport && !sheetOpen);
  }, [isDesktopViewport, sheetOpen]);
  // Badge count for the Saved button — re-read whenever the modal opens or closes.
  // Deliberately an effect, not useMemo: reading localStorage during render makes
  // the prerendered HTML (count 0) disagree with the client, and useMemo keyed on
  // an unrelated value is a lie to the dependency linter.
  const [savedCount, setSavedCount] = useState(0);
  useEffect(() => { setSavedCount(readSavedWheels().length); }, [showSaved]);

  // Loading a saved wheel replaces the live wheel outright — keep a snapshot so
  // it can be reversed within the undo window.
  const [loadUndoSnap, setLoadUndoSnap] = useState<{
    entries: WheelEntry[];
    title: string;
    options: WheelOptions;
    fromName: string;
    timerId: ReturnType<typeof setTimeout> | null;
  } | null>(null);

  // Don't leave a pending dismiss timer behind on unmount.
  useEffect(() => () => {
    if (loadUndoSnap?.timerId) clearTimeout(loadUndoSnap.timerId);
  }, [loadUndoSnap]);

  // Team-assignment state (/random-team-generator only).
  const [teamCount, setTeamCount] = useState(2);
  const [teams, setTeams] = useState<string[][]>(() => [[], []]);
  // Which team receives the next winner. Kept as its own counter rather than
  // derived from the assigned total so that clearing one team cannot make the
  // round-robin double back over a team that already has members.
  const [nextTeam, setNextTeam] = useState(0);
  const [teamsCopied, setTeamsCopied] = useState(false);

  // Range builder inputs (/number-wheel only).
  const [rangeMin, setRangeMin] = useState('1');
  const [rangeMax, setRangeMax] = useState('20');

  // Only entries with a positive weight go on the wheel. Weight 0 means "keep
  // this name in my list but leave it out of the draw", so it must not occupy a
  // slice and must never be selected.
  const activeIndexes = entries.reduce<number[]>((acc, e, i) => {
    if (isActive(e.weight)) acc.push(i);
    return acc;
  }, []);
  const namesForWheel = activeIndexes.map(i => entries[i].name);
  const weightsForWheel = activeIndexes.map(i => entries[i].weight);
  const colorsForWheel = activeIndexes.map(i => entries[i].color);
  const imagesForWheel = activeIndexes.map(i => entries[i].imageDataUrl);
  const allColors = entries.map(e => e.color);
  const allNames = entries.map(e => e.name);
  const allWeights = entries.map(e => e.weight);
  const weightedMode = !isUniform(allWeights);

  // SpinWheel reports the index it landed on within the arrays it was given,
  // which excludes the inactive entries. Everything downstream (auto-remove,
  // "remove and spin again") indexes into the full list, so the mapping has to
  // be read at spin-completion time — a ref, because handleSpinComplete is
  // memoised and would otherwise close over a stale array.
  const activeIndexesRef = useRef(activeIndexes);
  activeIndexesRef.current = activeIndexes;
  // Spin completion runs after an animation, so read the latest full entry by
  // index instead of closing over the list from the frame that started it.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // ── Save to localStorage — debounced to avoid INP hits on every keystroke ──
  // Typing into the entry list fires setEntries on every character, which
  // previously triggered a synchronous JSON.stringify + localStorage.setItem
  // on every render commit. At 50+ entries that can cost 2–5 ms of main-thread
  // time per keypress, which directly raises INP scores. 400 ms is the
  // standard debounce for "user has paused" and is imperceptible in practice.
  useEffect(() => {
    const id = setTimeout(() => {
      saveToStorage(entries, title, options, storageKeyPrefix);
    }, 400);
    return () => clearTimeout(id);
  }, [entries, title, options, storageKeyPrefix]);

  useEffect(() => {
    const id = setTimeout(() => {
      saveResults(storageKeyPrefix, results);
    }, 400);
    return () => clearTimeout(id);
  }, [results, storageKeyPrefix]);

  // ── Load from URL param or navigation state (highest priority) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shared = params.get('shared');
    if (shared) {
      const data = decodeShare(shared);
      if (data) {
        if (preferStoredState && hasStoredEntriesRef.current) return;
        setTitle(data.t);
        setTitleDraft(data.t);
        setEntries(data.entries);
        // A shared wheel owns its center-image choice. Clear a logo saved on
        // this browser when the sender did not include one, otherwise local
        // appearance state would leak into someone else's shared wheel.
        setOptions(prev => {
          const next = { ...prev };
          if (data.centerImageDataUrl) next.centerImageDataUrl = data.centerImageDataUrl;
          else delete next.centerImageDataUrl;
          return next;
        });
        return;
      }
    }
    const state = location.state as { template?: WheelTemplate } | null;
    if (state?.template) {
      setTitle(state.template.title);
      setTitleDraft(state.template.title);
      setEntries(state.template.names.map(name => ({ name, weight: 1 })));
      window.history.replaceState({}, '');
    }
  }, [location.state, preferStoredState]);

  // ── Entry manipulation helpers ──
  /**
   * Replace the name list, carrying entry metadata over by position.
   *
   * Positional carry-over is right for add/remove/bulk-edit, where slot N keeps
   * its meaning. It is NOT right for reordering — see handleReorder.
   */
  const setNames = useCallback((names: string[]) => {
    if (names.length > MAX_ENTRIES) names = names.slice(0, MAX_ENTRIES);
    setEntries(prev => names.map((name, i) => {
      const entry: WheelEntry = { name, weight: prev[i]?.weight ?? DEFAULT_WEIGHT };
      // Must be carried explicitly. Rebuilding the object without it is what
      // made Shuffle, Sort and even Add silently clear every custom colour.
      if (prev[i]?.color) entry.color = prev[i].color;
      if (prev[i]?.imageDataUrl) entry.imageDataUrl = prev[i].imageDataUrl;
      return entry;
    }));
  }, []);

  /**
   * Apply a permutation to the entries themselves.
   *
   * Shuffle and Sort go through here rather than setNames so that each name
   * keeps its own weight and colour. Reordering names alone left the weights
   * behind at their old positions, which quietly changed everyone's odds.
   */
  const handleReorder = useCallback((order: number[]) => {
    setEntries(prev => order.map(i => prev[i]).filter(Boolean));
  }, []);

  /** Atomically restore a full snapshot — used by Undo. */
  const handleRestoreEntries = useCallback((restored: WheelEntry[]) => {
    setEntries(restored.map(e => ({ ...e })));
  }, []);

  const handleColorChange = useCallback((index: number, color: string | undefined) => {
    // Clearing removes the key rather than storing undefined. The wheel reads a
    // missing colour as "use the palette slot", and an explicit undefined would
    // otherwise ride along into share links and saved slots as dead weight.
    setEntries(prev => prev.map((e, i) => {
      if (i !== index) return e;
      if (color === undefined) {
        const next = { ...e };
        delete next.color;
        return next;
      }
      return { ...e, color };
    }));
  }, []);

  const handleResetColors = useCallback(() => {
    setEntries(prev => prev.map(e => {
      if (!e.color) return e;
      const next = { ...e };
      delete next.color;
      return next;
    }));
  }, []);

  const handleWeightChange = useCallback((index: number, weight: number) => {
    setEntries(prev => prev.map((e, i) => i === index ? { ...e, weight: clampWeight(weight) } : e));
  }, []);

  /** Append imported entries, keeping any weights the file carried. */
  const handleImportEntries = useCallback((imported: { name: string; weight: number }[]) => {
    const normalized = normalizeEntries(imported) ?? [];
    setEntries(prev => clampEntries([
      ...prev,
      ...normalized,
    ]).kept);
  }, []);

  /** Append preprocessed local-image entries as one atomic state update. */
  const handleAddImageEntries = useCallback((uploaded: WheelEntry[]) => {
    const normalized = normalizeEntries(uploaded) ?? [];
    if (normalized.length === 0) return;
    setEntries(prev => clampEntries([...prev, ...normalized]).kept);
  }, []);

  /**
   * Append a single name from the name-list below the wheel.
   *
   * Skips duplicates (case-insensitive) and respects MAX_ENTRIES. Called via
   * the `registerAddName` prop so NameListPage can trigger it without a ref.
   */
  const handleAddName = useCallback((name: string) => {
    setEntries(prev => {
      if (prev.some(e => e.name.toLowerCase() === name.toLowerCase())) return prev;
      return clampEntries([...prev, { name, weight: DEFAULT_WEIGHT }]).kept;
    });
  }, []);

  // Register the handler with the parent (NameListPage) on mount.
  useEffect(() => {
    registerAddName?.(handleAddName);
  }, [registerAddName, handleAddName]);

  const handleSpinComplete = useCallback((name: string, wheelIndex: number) => {
    setIsSpinning(false);
    announce(`${t('wheel.winner')}: ${name}`, 'assertive');
    const stamp = { name, time: formatTime(new Date()) };
    setResults(prev => [...prev, stamp]);
    // Per-class history, separate from the page-level list. The page list mixes
    // every wheel opened at this URL together; a teacher needs 5A's record, not
    // this route's.
    if (activeSlotRef.current) appendSlotHistory(activeSlotRef.current, stamp);
    // Translate back to a position in the full entry list.
    const index = activeIndexesRef.current[wheelIndex] ?? wheelIndex;
    const winningEntry = entriesRef.current[index];
    onEmbeddedWinner?.({ name, index, imageDataUrl: winningEntry?.imageDataUrl });

    // Auto-switch the mobile entries panel to the Results tab.
    setMobileTab('results');

    if (teamMode) {
      // The winner joins the next team and leaves the wheel, so the round-robin
      // works through everyone without repeats. No winner popup: it would have
      // to be dismissed between every single assignment.
      setTeams(prev => {
        const next = prev.map(t => [...t]);
        const slot = nextTeam % Math.max(1, next.length);
        (next[slot] ??= []).push(name);
        return next;
      });
      setNextTeam(n => n + 1);
      setEntries(prev => prev.filter((_, i) => i !== index));
      return;
    }

    if (options.autoRemove) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      return;
    }
    if (options.winnerDisplay === 'banner') {
      bannerKeyRef.current += 1;
      setBannerWinner({ name, index, imageDataUrl: winningEntry?.imageDataUrl });
      return;
    }
    setWinner({ name, index, imageDataUrl: winningEntry?.imageDataUrl });
  }, [options.autoRemove, options.winnerDisplay, teamMode, nextTeam, announce, onEmbeddedWinner, t]);

  const handleSpinStateChange = useCallback((spinning: boolean) => {
    setIsSpinning(spinning);
    if (spinning) onEmbeddedSpinStart?.();
  }, [onEmbeddedSpinStart]);

  // ── Team mode helpers ──
  const applyTeamCount = useCallback((raw: number) => {
    const n = Math.min(MAX_TEAMS, Math.max(2, Math.round(raw) || 2));
    setTeamCount(n);
    // Resize without discarding assignments already made: shrinking folds the
    // orphaned members back onto the remaining teams rather than losing them.
    setTeams(prev => {
      const next: string[][] = Array.from({ length: n }, (_, i) => [...(prev[i] ?? [])]);
      prev.slice(n).flat().forEach((member, i) => next[i % n].push(member));
      return next;
    });
  }, []);

  const resetTeams = useCallback(() => {
    setTeams(Array.from({ length: teamCount }, () => []));
    setNextTeam(0);
  }, [teamCount]);

  /** Deal every remaining name out at once, for groups too big to spin one by one. */
  const assignRemaining = useCallback(() => {
    if (isSpinning) return;
    const pool = entries.filter(e => isActive(e.weight)).map(e => e.name);
    if (pool.length === 0) return;
    // Fisher–Yates so the deal order is not the list order.
    for (let i = pool.length - 1; i > 0; i--) {
      const j = randomInt(i + 1);
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    setTeams(prev => {
      const next = prev.map(t => [...t]);
      pool.forEach((name, k) => next[(nextTeam + k) % Math.max(1, next.length)].push(name));
      return next;
    });
    setNextTeam(n => n + pool.length);
    setResults(prev => [...prev, ...pool.map(name => ({ name, time: formatTime(new Date()) }))]);
    setEntries(prev => prev.filter(e => !isActive(e.weight)));
  }, [entries, isSpinning, nextTeam]);

  // ── Preset / range helpers ──
  const loadNames = useCallback((names: string[], weights?: number[]) => {
    setEntries(clampEntries(names.map((name, index) => ({
      name,
      weight: clampWeight(weights?.[index] ?? DEFAULT_WEIGHT),
    }))).kept);
  }, []);

  const loadRange = useCallback(() => {
    const lo = parseInt(rangeMin, 10);
    const hi = parseInt(rangeMax, 10);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return;
    const from = Math.min(lo, hi);
    const to = Math.max(lo, hi);
    const size = to - from + 1;
    if (size > MAX_RANGE_SIZE) return;
    loadNames(Array.from({ length: size }, (_, i) => String(from + i)));
  }, [rangeMin, rangeMax, loadNames]);

  const rangeSize = (() => {
    const lo = parseInt(rangeMin, 10);
    const hi = parseInt(rangeMax, 10);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return 0;
    return Math.abs(hi - lo) + 1;
  })();

  const handleSpinAgain = useCallback(() => {
    setWinner(null);
    setTimeout(() => wheelRef.current?.spin(), 50);
  }, []);

  const handleRemoveAndSpin = useCallback(() => {
    if (!winner) return;
    // Snapshot BEFORE the mutation, so undo rewinds to a list that still
    // contains the winner.
    undoHistory.pushSnapshot(entries, 'Remove winner');
    setEntries(prev => prev.filter((_, i) => i !== winner.index));
    setWinner(null);
    setTimeout(() => wheelRef.current?.spin(), 100);
  }, [winner, entries, undoHistory]);

  /**
   * Restore the newest snapshot.
   *
   * Shared by the toast button and Ctrl+Z so both do exactly the same thing —
   * two code paths for one action is how they drift apart.
   */
  const handleUndo = useCallback(() => {
    const restored = undoHistory.undo();
    if (restored) setEntries(restored);
  }, [undoHistory]);

  /**
   * Ctrl+Z / Cmd+Z, scoped to this page.
   *
   * Registered here rather than globally so it is gone on /gallery and the
   * other sub-pages, which have no entries to restore. The cleanup is what
   * enforces that — without it the listener would outlive the page and undo a
   * wheel the visitor is no longer looking at.
   *
   * Ignored while focus is in a text field, where Ctrl+Z already means "undo
   * my typing" and hijacking it would be worse than not offering it.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'z' && e.key !== 'Z') return;
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return;

      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement | null)?.isContentEditable) return;

      if (!undoHistory.canUndo) return;
      e.preventDefault();
      handleUndo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undoHistory, handleUndo]);

  const handleLoadSavedWheel = useCallback((
    loadedEntries: WheelEntry[],
    loadedTitle: string,
    slotName: string,
    loadedOptions: WheelOptions | undefined,
    slotId: string,
  ) => {
    // From here on, every spin is also recorded against this class.
    activeSlotRef.current = slotId;
    // Cancel any in-flight undo window from a previous load.
    if (loadUndoSnap?.timerId) clearTimeout(loadUndoSnap.timerId);

    // Snapshot current state BEFORE mutating.
    const prevEntries = entries;
    const prevTitle = title;
    const prevOptions = options;

    setEntries(loadedEntries);
    setTitle(loadedTitle);
    setTitleDraft(loadedTitle);
    // Slots saved before options were captured have none — leave the current
    // settings alone rather than silently resetting them. Merge over defaults so
    // option keys added after the slot was written still have a value.
    if (loadedOptions) {
      setOptions(normalizeOptions(loadedOptions));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const timerId = setTimeout(() => setLoadUndoSnap(null), UNDO_TIMEOUT_MS);
    setLoadUndoSnap({
      entries: prevEntries,
      title: prevTitle,
      options: prevOptions,
      fromName: slotName,
      timerId,
    });
  }, [entries, title, options, loadUndoSnap]);

  const handleLoadUndo = useCallback(() => {
    if (!loadUndoSnap) return;
    if (loadUndoSnap.timerId) clearTimeout(loadUndoSnap.timerId);
    setEntries(loadUndoSnap.entries);
    setTitle(loadUndoSnap.title);
    setTitleDraft(loadUndoSnap.title);
    setOptions(loadUndoSnap.options);
    setLoadUndoSnap(null);
  }, [loadUndoSnap]);

  const dismissLoadUndo = useCallback(() => {
    if (loadUndoSnap?.timerId) clearTimeout(loadUndoSnap.timerId);
    setLoadUndoSnap(null);
  }, [loadUndoSnap]);

  const handleLoadTemplate = (tmpl: WheelTemplate) => {
    setTitle(tmpl.title);
    setEntries(tmpl.names.map(name => ({ name, weight: 1 })));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getShareUrl = useCallback(() => {
    const encoded = encodeShare(title, entries, options.centerImageDataUrl);
    return buildSharedWheelUrl(window.location.origin, '/', encoded);
  }, [title, entries, options.centerImageDataUrl]);

  /**
   * A share link carries the entire entry list in the URL, so it grows with the
   * wheel: roughly 2.9 KB at 100 entries and 1.4 MB at 50,000. Past ~8 KB the
   * link stops surviving mail clients and chat apps, which truncate silently —
   * the recipient opens a wheel missing most of its entries and has no way to
   * tell. Better to say so here and point at Export, which has no size limit.
   */
  const shareTooLarge = useMemo(
    () => shareUrlByteLength(encodeShare(title, entries, options.centerImageDataUrl)) > MAX_SHARE_URL_BYTES,
    [title, entries, options.centerImageDataUrl],
  );
  const shareLimitReason = options.centerImageDataUrl || entries.some(entry => entry.imageDataUrl)
    ? t('wheel.shareTooLargeImages')
    : t('wheel.shareTooLargeList');

  const handleSaveImage = useCallback(() => {
    const ok = downloadWheelImage(wheelRef.current?.getCanvas() ?? null, title, entries.length);
    if (!ok) return;
    setImageSaved(true);
    setTimeout(() => setImageSaved(false), 2500);
  }, [title, entries.length]);

  const handleShare = async () => {
    const url = getShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch { /* clipboard denied */ }
  };

  const commitTitle = () => {
    setTitle(titleDraft.trim() || (lang === 'en' ? 'My Wheel' : 'TheNameWheel'));
    setEditingTitle(false);
  };


  return (
    <>
      {/* ── Wheel Section ── */}
      <section className="max-w-[1800px] mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-start">

          {/* Wheel column — flex-1 so it takes everything the sidebar doesn't;
              min-w-0 lets it shrink below its content width instead of overflowing. */}
          <div className="flex flex-col items-center gap-4 w-full lg:flex-1 lg:min-w-0">
            {/* Title row */}
            <div className="flex flex-wrap items-center gap-2 w-full max-w-[600px] lg:max-w-none">
              {editingTitle ? (
                <div className="flex items-center gap-2 flex-1">
                  <input
                    ref={titleInputRef}
                    value={titleDraft}
                    dir="auto"
                    maxLength={MAX_WHEEL_TITLE_LENGTH}
                    aria-label={t('wheel.titleLabel')}
                    onChange={e => setTitleDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                    className="flex-1 text-xl font-extrabold text-gray-800 dark:text-white bg-transparent border-b-2 border-violet-400 outline-none py-0.5"
                    autoFocus
                  />
                  <button onClick={commitTitle} aria-label={t('wheel.saveTitle')} className="p-1.5 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors">
                    <Check size={18} />
                  </button>
                </div>
              ) : (
                <button
                  className="flex items-center gap-1.5 group"
                  onClick={() => { setTitleDraft(title); setEditingTitle(true); }}
                  aria-label={t('wheel.editTitle', { title })}
                >
                  <TitleTag dir="auto" className="text-xl font-extrabold text-gray-800 dark:text-white group-hover:text-violet-500 transition-colors">{title}</TitleTag>
                  <Pencil size={14} className="text-gray-300 dark:text-gray-600 group-hover:text-violet-400 transition-colors" />
                </button>
              )}
              <div className="ms-auto flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                {/* Exports the wheel as it stands — for printing a rota,
                    evidencing a draw, or dropping into a slide. Distinct from
                    the winner card, which shares a result rather than the
                    options. Never disabled by list size: a 50,000-entry wheel
                    still renders to an image, unlike a share link. */}
                <button
                  onClick={handleSaveImage}
                  disabled={entries.length === 0}
                  title={t('wheel.saveImage')}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {imageSaved ? <Check size={13} /> : <ImageDown size={13} />}
                  <span className="hidden sm:inline">{imageSaved ? t('wheel.saved') : t('wheel.image')}</span>
                </button>
                <button
                  onClick={handleShare}
                  disabled={shareTooLarge}
                  title={shareTooLarge ? shareLimitReason : t('wheel.share')}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed ${shareCopied ? 'bg-green-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                >
                  <Share2 size={13} />
                  <span className="hidden sm:inline">{shareCopied ? t('wheel.shared') : t('wheel.share')}</span>
                </button>
                <button
                  onClick={() => setShowQR(true)}
                  disabled={shareTooLarge}
                  title={shareTooLarge ? shareLimitReason : t('wheel.shareQr')}
                  className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <QrCode size={13} />
                  <span className="hidden sm:inline">{t('wheel.qr')}</span>
                </button>
                {!embedMode && (
                  <button
                    onClick={() => setShowSaved(true)}
                    title={t('wheel.savedWheels')}
                    className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <BookmarkIcon size={13} />
                    <span className="hidden sm:inline">{t('wheel.saved')}</span>
                    {savedCount > 0 && (
                      <span className="bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                        {savedCount}
                      </span>
                    )}
                  </button>
                )}
                {!embedMode && (
                  <button
                    onClick={() => setShowEmbed(true)}
                    disabled={shareTooLarge}
                    title={shareTooLarge ? shareLimitReason : t('wheel.embed')}
                    className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs font-semibold rounded-lg transition-all bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Code2 size={13} />
                    <span className="hidden sm:inline">{t('wheel.embed')}</span>
                  </button>
                )}
                <button
                  ref={optionsBtnRef}
                  onClick={() => setShowOptions(true)}
                  title={t('wheel.options')}
                  className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all"
                >
                  <Settings size={16} />
                </button>
              </div>
            </div>

            {/* Quick-load presets (/number-wheel, /random-letter-wheel) */}
            {(presets?.length || rangeBuilder) && (
              <div className="w-full max-w-[600px] lg:max-w-none flex flex-wrap items-center gap-2 text-xs">
                {presets?.length ? (
                  <>
                    <span className="font-semibold text-gray-500 dark:text-gray-400">{t('wheel.quickLoad')}</span>
                    {presets.map(p => (
                      <button
                        key={p.label}
                        onClick={() => loadNames(p.names, p.weights)}
                        disabled={isSpinning}
                        className="px-2.5 py-1 rounded-lg font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </>
                ) : null}

                {rangeBuilder && (
                  <div className="flex items-center gap-1.5 ms-auto">
                    <label className="text-gray-500 dark:text-gray-400" htmlFor="range-min">Min</label>
                    <input
                      id="range-min"
                      type="number"
                      value={rangeMin}
                      onChange={e => setRangeMin(e.target.value)}
                      className="w-16 px-1.5 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                    <label className="text-gray-500 dark:text-gray-400" htmlFor="range-max">Max</label>
                    <input
                      id="range-max"
                      type="number"
                      value={rangeMax}
                      onChange={e => setRangeMax(e.target.value)}
                      className="w-16 px-1.5 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                    <button
                      onClick={loadRange}
                      disabled={isSpinning || rangeSize === 0 || rangeSize > MAX_RANGE_SIZE}
                      title={rangeSize > MAX_RANGE_SIZE ? `Ranges are capped at ${MAX_RANGE_SIZE} numbers` : 'Load range'}
                      className="px-2.5 py-1 rounded-lg font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Load range
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Team count (/random-team-generator) */}
            {teamMode && (
              <div className="w-full max-w-[600px] lg:max-w-none flex flex-wrap items-center gap-2 text-xs">
                <label className="font-semibold text-gray-500 dark:text-gray-400" htmlFor="team-count">
                  {t('wheel.teamCount')}
                </label>
                <input
                  id="team-count"
                  type="number"
                  min={2}
                  max={MAX_TEAMS}
                  value={teamCount}
                  onChange={e => applyTeamCount(parseInt(e.target.value, 10))}
                  className="w-16 px-1.5 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
                />
                <span className="text-gray-500 dark:text-gray-400">2–{MAX_TEAMS}</span>
                <div className="ms-auto flex items-center gap-2">
                  <button
                    onClick={assignRemaining}
                    disabled={isSpinning || namesForWheel.length === 0}
                    className="px-2.5 py-1 rounded-lg font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('wheel.assignRest')}
                  </button>
                  <button
                    onClick={resetTeams}
                    disabled={isSpinning || teams.every(tm => tm.length === 0)}
                    className="px-2.5 py-1 rounded-lg font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('wheel.resetTeams')}
                  </button>
                </div>
              </div>
            )}

            {/* Wheel card — no fixed width/height. Tight padding on desktop so the
                wheel gets nearly every pixel of the card. */}
            {/* CLS FIX: min-height reserves layout space before ResizeObserver fires.
                Without it, the card collapses to zero height during SSR/prerender
                (the canvas reports 0px until JS measures the container), then
                jumps to full height on client hydration — that shift scores as CLS.
                320px is the smallest the wheel ever renders (narrow mobile); the
                actual height is always larger, so this never clips content. */}
            <div
              className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 sm:p-4 lg:p-3 flex flex-col items-center gap-3 sm:gap-4 w-full max-w-[600px] lg:max-w-none"
              style={{ minHeight: '320px' }}
            >
              {bannerWinner && (
                <WinnerBanner
                  key={bannerKeyRef.current}
                  winner={bannerWinner.name}
                  winnerImage={bannerWinner.imageDataUrl}
                  onDismiss={() => setBannerWinner(null)}
                />
              )}
              <SpinWheel
                ref={wheelRef}
                names={namesForWheel}
                weights={weightsForWheel}
                colors={colorsForWheel}
                images={imagesForWheel}
                centerImage={options.centerImageDataUrl}
                palette={options.palette}
                rimStyle={options.rimStyle}
                equalSegments={!options.weightedSegments}
                isSpinning={isSpinning}
                setIsSpinning={handleSpinStateChange}
                onSpinComplete={handleSpinComplete}
                soundEnabled={options.soundEnabled}
                spinDuration={options.spinDuration}
                fontFamily={FONT_STACK_MAP[options.fontFamily]}
                fontWeight={options.fontWeight}
                fontSize={options.fontSize}
                fill
              />
              <button
                ref={spinBtnRef}
                onClick={async () => {
                  if (isSpinning) return;
                  // INP FIX: Call announce() first so the live-region update is
                  // queued, then yield to the browser. The yield lets the browser
                  // paint the button's :disabled / grey state (applied by React
                  // immediately via setIsSpinning inside spin()) before the heavy
                  // angle-calculation math in spin() starts. Without the yield,
                  // the math runs in the same task as the click handler and the
                  // button stays visually active until the task finishes — which
                  // is what registers as a long INP duration.
                  announce(t('wheel.spinningAria'), 'polite');
                  await yieldToBrowser();
                  wheelRef.current?.spin();
                }}
                disabled={isSpinning || namesForWheel.length === 0}
                aria-busy={isSpinning}
                aria-label={
                  isSpinning ? t('wheel.spinningAria') :
                  namesForWheel.length === 0 ? t('wheel.emptyAria') :
                  t('wheel.spinAria')
                }
                className="spin-btn flex items-center justify-center gap-2.5 px-10 py-3.5 rounded-2xl font-extrabold text-white text-base tracking-wide transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-violet-400 focus-visible:ring-offset-2"
                style={isSpinning ? SPIN_BTN_STYLE_BUSY : SPIN_BTN_STYLE_IDLE}
              >
                <RotateCcw size={18} className={isSpinning ? 'animate-spin' : ''} />
                {isSpinning ? t('wheel.spinning') : t('wheel.spin')}
              </button>
              {/* Projector mode — fullscreen for classroom use. Hidden on mobile
                  where the concept does not apply. */}
              <button
                type="button"
                onClick={() => { try { document.documentElement.requestFullscreen(); } catch { /* unsupported */ } }}
                className="hidden sm:inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors mt-1"
                aria-label={t('wheel.projector')}
              >
                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                  <polyline points="1 5 1 1 5 1" /><polyline points="11 1 15 1 15 5" />
                  <polyline points="15 11 15 15 11 15" /><polyline points="5 15 1 15 1 11" />
                </svg>
                {t('wheel.projector')}
              </button>
            </div>

            {/* Mobile inline entries panel — always visible below lg.
                Replaces the old sheet-opener button so entries are always in
                view without a tap.
                min-height + contain:layout reserves space before mount, preventing
                the CLS (Cumulative Layout Shift) caused by the component painting
                late after hydration. */}
            <div
              className="lg:hidden w-full max-w-[600px]"
              style={{ minHeight: '180px', contain: 'layout style' }}
            >
              <MobileEntriesPanel
                entries={entries}
                results={results}
                onAdd={(name) => setNames([...allNames, name])}
                onAddImageEntries={handleAddImageEntries}
                onWeightChange={handleWeightChange}
                onRemove={(i) => setEntries(prev => prev.filter((_, idx) => idx !== i))}
                onShuffle={() => {
                  // Fire the same shuffle the desktop panel uses.
                  // Reuse the permutation approach: trigger the parent's reorder.
                  if (allNames.length < 2) return;
                  const order = allNames.map((_, idx) => idx);
                  for (let k = order.length - 1; k > 0; k--) {
                    const j = randomInt(k + 1);
                    [order[k], order[j]] = [order[j], order[k]];
                  }
                  handleReorder(order);
                }}
                onSort={() => {
                  if (allNames.length < 2) return;
                  const order = allNames
                    .map((name, idx) => ({ name, idx }))
                    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                    .map(x => x.idx);
                  handleReorder(order);
                }}
                onClear={() => setEntries([])}
                onImportEntries={handleImportEntries}
                isSpinning={isSpinning}
                palette={options.palette}
                activeTab={mobileTab}
                onTabChange={setMobileTab}
              />
            </div>


            {/* Sheet opener — kept for lg-only reference but hidden since the
                inline panel replaced it. Still here as the onboarding tour
                target ref. */}
            <button
              ref={sheetOpenerRef}
              onClick={() => setSheetOpen(true)}
              aria-expanded={sheetOpen}
              aria-controls="entries-panel"
              className="hidden lg:hidden items-center justify-center gap-2 w-full max-w-[600px] py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <ListIcon size={15} />
              {entries.length === 0
                ? t('entries.empty')
                : `${entries.length} ${entries.length === 1 ? t('entries.onWheel') : t('entries.onWheelPlural')} — edit`}
            </button>

            <div className="hidden lg:flex flex-wrap items-center justify-center gap-2">
              {entries.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {namesForWheel.length} {namesForWheel.length === 1 ? t('entries.onWheel') : t('entries.onWheelPlural')}
                  {entries.length !== namesForWheel.length && ` · ${t('entries.inactiveCount', { count: entries.length - namesForWheel.length })}`}
                  {options.autoRemove && ` · ${t('wheel.autoRemove')}`}
                </p>
              )}
              {/* Says out loud that the slices are no longer an even split, so
                  nobody reads uneven segments as a rendering bug. */}
              {weightedMode && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                  title={options.weightedSegments
                    ? t('wheel.weightedVisible')
                    : t('wheel.weightedHidden')}
                >
                  <Scale size={11} aria-hidden="true" />
                  {t('wheel.weightedMode')}
                  {!options.weightedSegments && ` (${t('wheel.equalSlices')})`}
                </span>
              )}
              {/* The one claim a sampled wheel has to make explicitly. Someone
                  running a 5,000-ticket raffle must not be left to infer from a
                  240-slice wheel that only 240 tickets are in the draw. */}
              {isSampled(entries.length) && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300"
                  title={t('wheel.sampledTitle', { shown: MAX_DRAWN_SEGMENTS, total: entries.length.toLocaleString() })}
                >
                  <Layers size={11} aria-hidden="true" />
                  {t('wheel.sampledLabel', { shown: MAX_DRAWN_SEGMENTS, total: entries.length.toLocaleString() })}
                </span>
              )}
            </div>

            {/* Team assignments (/random-team-generator) */}
            {teamMode && teams.some(tm => tm.length > 0) && (
              <div className="w-full max-w-[600px] lg:max-w-none">
                <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 mb-2">{t('wheel.teams')}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {teams.map((members, i) => (
                    <div
                      key={i}
                      className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 p-3"
                    >
                      <p className="text-xs font-bold text-violet-600 dark:text-violet-400 mb-1.5">
                        {t('wheel.team', { number: i + 1 })}
                        <span className="ms-1 font-normal text-gray-500 dark:text-gray-400">({members.length})</span>
                      </p>
                      <ul className="space-y-0.5">
                        {members.length === 0
                          ? <li className="text-xs text-gray-300 dark:text-gray-600">{t('wheel.empty')}</li>
                          : members.map((m, k) => (
                              <li key={k} dir="auto" className="text-xs text-gray-700 dark:text-gray-200 truncate">{m}</li>
                            ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <button
                  onClick={async () => {
                    const text = teams
                      .map((members, i) => `${t('wheel.team', { number: i + 1 })}: ${members.join(', ')}`)
                      .join('\n');
                    try {
                      await navigator.clipboard.writeText(text);
                      setTeamsCopied(true);
                      setTimeout(() => setTeamsCopied(false), 2000);
                    } catch { /* clipboard denied */ }
                  }}
                  className={`mt-3 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    teamsCopied
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {teamsCopied ? <CheckCheck size={13} /> : <Copy size={13} />}
                  {teamsCopied ? t('wheel.copiedTeams') : t('wheel.copyTeams')}
                </button>
              </div>
            )}
          </div>

          {sheetOpen && (
            <div
              onClick={() => setSheetOpen(false)}
              aria-hidden="true"
              className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px]"
            />
          )}

          {/*
            Entries — a sidebar on desktop, a bottom sheet below `lg`.

            Stacking the panel under the wheel at a fixed 380px meant a phone
            showed a cramped wheel AND a cramped list, with the SPIN button
            pushed off-screen between them. The sheet gives the wheel the whole
            viewport and puts the list one tap away, which is the order things
            are actually used in: set up once, then spin repeatedly.

            One EntriesPanel instance, repositioned by CSS — rendering a second
            copy for mobile would double the state and let the two drift.
          */}
          <div
            ref={entriesPanelRef}
            id="entries-panel"
            className={`
              flex flex-col flex-none overflow-hidden
              lg:w-[340px] lg:max-w-[350px] lg:sticky lg:top-20
              lg:h-[calc(100svh-7rem)] lg:min-h-[520px] lg:max-h-[820px]
              lg:static lg:translate-y-0 lg:shadow-none lg:rounded-none lg:bg-transparent
              max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-40
              max-lg:h-[72vh] max-lg:rounded-t-3xl max-lg:shadow-2xl
              max-lg:bg-white max-lg:dark:bg-gray-900
              max-lg:transition-transform max-lg:duration-300 max-lg:ease-out
              ${sheetOpen ? 'max-lg:translate-y-0' : 'max-lg:translate-y-full'}
            `}
            // Hidden from assistive tech while off-screen, so a screen reader
            // does not walk a list the sighted user cannot see.
            aria-hidden={!isDesktopViewport && !sheetOpen ? true : undefined}
          >
            {/* Drag affordance + close, sheet only. */}
            <div className="lg:hidden flex items-center justify-between px-4 pt-3 pb-1 flex-none">
              <span aria-hidden="true" className="absolute start-1/2 -translate-x-1/2 top-2 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                {entries.length} {entries.length === 1 ? t('entries.onWheel') : t('entries.onWheelPlural')}
              </span>
              <button
                onClick={() => setSheetOpen(false)}
                aria-label={t('wheel.closeEntries')}
                className="p-1.5 -me-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <EntriesPanel
              names={allNames}
              weights={allWeights}
              onWeightChange={handleWeightChange}
              setNames={setNames}
              onImportEntries={handleImportEntries}
              onAddImageEntries={handleAddImageEntries}
              entries={entries}
              onReorder={handleReorder}
              onRestoreEntries={handleRestoreEntries}
              onSnapshot={undoHistory.pushSnapshot}
              colors={allColors}
              onColorChange={handleColorChange}
              isSpinning={isSpinning}
              results={results}
              onClearResults={() => setResults([])}
              wheelTitle={title}
              palette={options.palette}
            />
          </div>
        </div>
      </section>

      {/* ── SEO Text Block removed ──────────────────────────────────────
          Replaced by the "What Is TheNameWheel?" entity/SGE section further
          down the page (inside showLandingSections). That block covers the
          same ground with structured SoftwareApplication schema, branded
          feature bullets, and data-speakable attributes — keeping both here
          would split the entity signal across two competing sections. */}

      {/* Resume banner — below the wheel so it never shifts the wheel upward.
          Returning users see it immediately after the wheel; new users never
          see it at all. This placement eliminates CLS from the banner. */}
      {showLandingSections && (
        <ResumeWheelBanner
          onLoad={(wheel) => {
            handleLoadSavedWheel(
              wheel.entries,
              wheel.name,
              wheel.name,
              wheel.options,
              wheel.id,
            );
          }}
          onOpenSaved={() => setShowSaved(true)}
        />
      )}

      {showLandingSections && lang !== 'en' && (
        <>
          <section className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 py-12">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-tight">
                {content.h1}
              </h1>
              {/* <!-- SEO: French and Dutch landing subheadings use their own locale copy. --> */}
              {showLocalizedHome && has('tool.home.subtitle') && (
                <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
                  {t('tool.home.subtitle')}
                </h2>
              )}
              <p data-speakable="true" className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                {content.body[0]}
              </p>
              {/* <!-- SEO: explicit English switch clears the remembered language preference. --> */}
              {['th', 'fr', 'nl'].includes(lang) && (
                <a href="/" hrefLang="en" lang="en" onClick={() => rememberLang('en')}
                  className="inline-block mt-4 font-semibold text-violet-700 dark:text-violet-400 hover:underline">
                  🌐 English version
                </a>
              )}
            </div>
          </section>
          {showLocalizedHome && <MainToolLocalizedContent tool="home" />}
        </>
      )}

      {/* Landing-page content. Preset pages (/yes-no-wheel, /classroom-name-picker,
          /giveaway-wheel) pass showLandingSections={false} and supply their own
          audience-specific copy, so this does not get duplicated across URLs.
          The content remains in the server-rendered HTML; content-visibility
          only lets supporting browsers defer offscreen layout and paint work. */}
      {showLandingSections && lang === 'en' && (
        <div style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 6000px' }}>
        {/* ── Hero strip ────────────────────────────────────────────────────
            Sits above the rest, alongside the headline on desktop and centred
            above it below 640px. Decorative: the working wheel is already on
            the page above this, so this exists to say what the site is to
            someone who has just landed, not to be used. */}
        <section className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center gap-8 sm:gap-12">
              <HeroWheelAnimation className="flex-shrink-0" />
              <div className="text-center sm:text-start">
                <span className="block text-xs font-semibold text-violet-700 dark:text-violet-400 uppercase tracking-wider mb-2">FREE RANDOMIZER</span>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
                  {content.h1}
                </h1>
                {/* <!-- SEO: supporting heading and direct language-switch links. --> */}
                <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">Pick a Random Name in Seconds</h2>
                {/* AEO-3: data-speakable marks the hero description for the Speakable schema */}
                {content.body.map((para, i) => (
                  <p
                    key={i}
                    data-speakable={i === 0 ? "true" : undefined}
                    className={`mt-3 leading-relaxed max-w-lg ${i === 0 ? "text-gray-600 dark:text-gray-300" : "text-sm text-gray-500 dark:text-gray-400"}`}
                  >
                    {para}
                  </p>
                ))}
                <nav aria-label="Homepage languages" className="mt-4 flex flex-wrap justify-center sm:justify-start gap-4 text-sm text-violet-700 dark:text-violet-400">
                  <a href="/th/" hrefLang="th" lang="th" className="hover:underline">ภาษาไทย</a>
                  <a href="/fr/" hrefLang="fr" lang="fr" className="hover:underline">Français</a>
                  <a href="/nl/" hrefLang="nl" lang="nl" className="hover:underline">Nederlands</a>
                </nav>
              </div>
            </div>
          </div>
        </section>

        {/* ── Proof points ── */}
        <section className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 pt-12 pb-2">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              {PROOF_POINTS.map(p => (
                <div key={p.label} className="flex flex-col items-center gap-1">
                  <dt className="sr-only">{p.label}</dt>
                  {/* Number — always on the same line across all cards */}
                  <dd className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-none">
                    {p.value}
                  </dd>
                  {/* Label — wraps below, centered, colour subdued */}
                  <dd className="text-xs sm:text-[13px] text-gray-500 dark:text-gray-400 leading-snug max-w-[10rem]">
                    {p.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ── Use cases ── */}
        <UseCasesSection />


        {/* ── Technical Specs ───────────────────────────────────────────
            Server-rendered semantic HTML for AI crawlers (ChatGPT, Perplexity,
            Claude web) to cite when recommending random picker tools.
            Every claim maps to verifiable code in src/utils/random.ts. */}
        <TechnicalSpecs />

        {/* ── Trust badges ────────────────────────────────────────────
            Immediately below the spec grid so the claim ("names
            stay local by default") is right next to the way to verify it.
            Each badge links to /privacy#how-we-proved-it. */}
        <div className="py-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700">
          <TrustBadges />
        </div>

        {/* ── Purpose-built tools ── */}
        <PurposeBuiltTools />

        {/* ── How it works ──────────────────────────────────────────────────
            indexed URLs do not carry identical copy. The intro paragraph folds
            in the keyword surface previously carried by the 8-box UseCases
            grid, which was deleted to eliminate repetition. */}
        <section className="py-14 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-2 text-center">
              {/* <!-- SEO: descriptive instructions heading for the name wheel. --> */}
              How to Use the Name Wheel
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-10 text-center max-w-xl mx-auto">
              From classroom cold-calls to live prize draws &mdash; add your entries,
              spin, and let the wheel decide.
            </p>

            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-0">
              {HOME_STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.title}>
                    {/* Step card */}
                    <div className="flex flex-col items-center text-center flex-1 px-2 group">
                      <div className="relative mb-3">
                        <div className="w-14 h-14 rounded-2xl bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800 flex items-center justify-center group-hover:bg-violet-100 dark:group-hover:bg-violet-900/40 group-hover:scale-110 transition-all duration-200 shadow-sm">
                          <Icon size={24} className="text-violet-600 dark:text-violet-400" />
                        </div>
                        <span className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                          {i + 1}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-1">{step.title}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed max-w-[180px]">
                        {step.body}
                      </p>
                    </div>
                    {/* Connector arrow (not after last step) */}
                    {i < HOME_STEPS.length - 1 && (
                      <>
                        <div className="hidden sm:flex items-center justify-center flex-shrink-0 sm:mt-4 px-1" aria-hidden="true">
                          <ChevronRight size={20} className="text-violet-300 dark:text-violet-600 how-arrow" />
                        </div>
                        <div className="flex sm:hidden items-center justify-center" aria-hidden="true">
                          <ChevronDown size={20} className="text-violet-300 dark:text-violet-600 how-arrow" />
                        </div>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <p className="mt-10 text-sm text-gray-500 dark:text-gray-400 text-center">
              Want the longer version, with bulk import, weighting and sharing?{' '}
              <Link to="/how-to/" className="text-violet-600 dark:text-violet-400 font-semibold hover:underline">
                Read the full guide
              </Link>.
            </p>
          </div>
        </section>

        {/* ── Linked Wheels spotlight ── */}
        <LinkedWheelsSpotlight />

        {/* ── Gallery ── */}
        <section className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">{t('gallery.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('gallery.subtitle')}</p>
            </div>
            <GallerySection onLoadWheel={handleLoadTemplate} limit={8} />
            <div className="mt-6 text-center">
              <Link to="/gallery/" className="text-sm font-semibold text-violet-700 dark:text-violet-400 hover:text-violet-800 transition-colors">
                {t('gallery.viewAll')}
              </Link>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 py-14">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">{t('faq.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('faq.subtitle')}</p>
            </div>
            <FaqSection items={homeFaqs()} />
            <div className="mt-8 text-center">
              <Link to="/faq/" className="text-sm font-semibold text-violet-700 dark:text-violet-400 hover:text-violet-800 transition-colors">
                Read all {FAQS.length} frequently asked questions →
              </Link>
            </div>
          </div>
        </section>


        {/* ── About & Key Features (Entity / SGE section) ──────────────────────
            Structured semantic block written specifically for Google AI Overview
            ingestion. Every claim is tied explicitly to "TheNameWheel" so LLMs
            map the feature set to this entity rather than a generic category or
            a competitor. Uses SoftwareApplication itemscope so structured-data
            parsers pick up the feature list without requiring a separate JSON-LD
            block. Short sentences + bold list = the format AI Overviews favour. */}
        <section className="bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section heading */}
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold text-violet-700 dark:text-violet-400 uppercase tracking-widest mb-3">
                About This Tool
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white leading-tight">
                What Is TheNameWheel?
              </h2>
              <p className="mt-4 text-base text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
                <strong>TheNameWheel</strong> is a free, browser-based{' '}
                <strong>random name picker</strong> and interactive{' '}
                <strong>custom spinner wheel</strong> — built for anyone who
                needs a fast, fair, and fun way to make decisions. No account.
                No download. No friction. Just spin.
              </p>
            </div>

            {/* Feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" itemProp="featureList">
              {[
                {
                  emoji: '🎡',
                  title: 'Custom Spinner Wheel',
                  body: 'Build your own digital wheel in seconds. Add any names, words, or options you choose — TheNameWheel adapts to any use case.',
                },
                {
                  emoji: '🎲',
                  title: 'Browser-Based Random Picker',
                  body: 'Equal-weight entries have equal odds. Supported browsers use Web Crypto with rejection sampling, with a fallback where it is unavailable.',
                },
                {
                  emoji: '⚡',
                  title: 'Zero Sign-Up Required',
                  body: 'TheNameWheel is fully functional the moment you land on the page. No registration, no email, no paywall — ever.',
                },
                {
                  emoji: '🏫',
                  title: 'Interactive Classroom Tool',
                  body: 'Teachers use TheNameWheel to randomly call on students, assign groups, or gamify participation — all without leaving the browser.',
                },
                {
                  emoji: '🧑‍💼',
                  title: 'Digital Decision Maker',
                  body: "Can't choose between options? Let TheNameWheel decide. It's the unbiased, instant tiebreaker for teams, families, and individuals.",
                },
                {
                  emoji: '📋',
                  title: 'Bulk Name Import',
                  body: 'Paste a list of dozens or hundreds of names at once. TheNameWheel handles large entries smoothly — ideal for raffles and class rosters.',
                },
                {
                  emoji: '💾',
                  title: 'Save & Reuse Your Wheel',
                  body: 'Set up your wheel once and reuse it any time. TheNameWheel remembers your entries so you never start from scratch.',
                },
                {
                  emoji: '📱',
                  title: 'Works on Any Device',
                  body: 'Fully responsive and mobile-friendly. TheNameWheel runs seamlessly on phones, tablets, and desktops — no app needed.',
                },
              ].map((feat) => (
                <div
                  key={feat.title}
                  className="flex items-start gap-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:border-violet-200 dark:hover:border-violet-700 transition-all"
                >
                  <span className="text-2xl flex-shrink-0 leading-none mt-0.5" aria-hidden="true">
                    {feat.emoji}
                  </span>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white text-sm mb-1">
                      {feat.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {feat.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Closing tagline — mirrors meta description for AI Overview pull */}
            <p className="mt-10 text-center text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl mx-auto" data-speakable="true">
              <strong>TheNameWheel</strong> — the free random name picker and
              custom spinner wheel. Whether you're a teacher, a streamer running
              a live giveaway, or a team lead assigning tasks fairly, this is the
              purpose-built, privacy-first tool for the job.{' '}
              <strong>Spin fair. Decide fast.</strong>
            </p>
          </div>
        </section>

        {/* ── Cross-links ── */}
        <section className="bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-white text-center mb-8">Explore More</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/how-to/" className="block bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:border-violet-200 dark:hover:border-violet-700 transition-all">
                <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-1">How to Use the Wheel</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Step-by-step guide covering entries, spinning, options, and sharing.</p>
              </Link>
              <Link to="/gallery/" className="block bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:border-violet-200 dark:hover:border-violet-700 transition-all">
                <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-1">Template Gallery</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Browse {TEMPLATES.length} ready-made wheels — days, dice, yes/no, school subjects, party games, and more.</p>
              </Link>
              <Link to="/about/" className="block bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 hover:shadow-md hover:border-violet-200 dark:hover:border-violet-700 transition-all">
                <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-1">About TheNameWheel</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">Learn the story and values behind this free, local-by-default random picker — and why we built it for classrooms and everyone else.</p>
              </Link>
            </div>
          </div>
        </section>
        </div>
      )}

      {/* Modals */}
      {showOptions && (
        <OptionsModal
          options={options}
          onChange={setOptions}
          onClose={() => setShowOptions(false)}
          customColorCount={entries.filter(e => e.color).length}
          onResetColors={handleResetColors}
        />
      )}
      {winner && (
        <WinnerModal
          winner={winner.name}
          winnerImage={winner.imageDataUrl}
          wheelTitle={title}
          showConfetti={options.showConfetti}
          onClose={() => setWinner(null)}
          onSpinAgain={handleSpinAgain}
          onRemoveAndSpin={handleRemoveAndSpin}
        />
      )}
      {/* Banner-mode confetti — the modal has its own, this covers the banner path */}
      {bannerWinner && options.showConfetti && <Confetti />}
      {/* Only for actions taken outside the entries panel — removing a winner
          from the modal. The panel renders its own toast for its own actions,
          so the two never appear together and every action has exactly one
          visible undo affordance. Ctrl+Z reaches both. */}
      {undoHistory.isFresh && undoHistory.lastActionLabel === 'Remove winner' && (
        <UndoToast label={undoHistory.lastActionLabel} onUndo={handleUndo} />
      )}
      {!embedMode && showSaved && (
        <Suspense fallback={null}>
          <SavedWheelsModal
            currentEntries={entries}
            currentTitle={title}
            currentOptions={options}
            onLoad={handleLoadSavedWheel}
            onClose={() => setShowSaved(false)}
          />
        </Suspense>
      )}

      {/* Load undo — page-level toast, auto-dismisses with the shared timeout */}
      {loadUndoSnap && (
        <div
          key={String(loadUndoSnap.timerId)}
          role="status"
          aria-live="polite"
          className="animate-undo-in fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 w-[calc(100%-2rem)] max-w-sm px-3 py-2.5 rounded-xl shadow-lg bg-gray-900 dark:bg-gray-700 text-white overflow-hidden"
        >
          <span className="flex-1 min-w-0 text-xs font-medium truncate">
            Loaded “{loadUndoSnap.fromName}”
          </span>
          <button
            onClick={handleLoadUndo}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-violet-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
          >
            <RotateCcw size={12} /> Undo
          </button>
          <button
            onClick={dismissLoadUndo}
            aria-label="Dismiss"
            title="Dismiss"
            className="flex-shrink-0 p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
          >
            <X size={12} />
          </button>
          <span
            className="animate-shrink absolute bottom-0 start-0 h-0.5 bg-violet-400"
            style={{ animationDuration: `${UNDO_TIMEOUT_MS}ms` }}
            aria-hidden="true"
          />
        </div>
      )}
      {!embedMode && showEmbed && (
        <EmbedModal
          entries={entries}
          title={title}
          options={options}
          onClose={() => setShowEmbed(false)}
          onShowQR={() => { setShowEmbed(false); setShowQR(true); }}
        />
      )}
      {showQR && (
        <React.Suspense fallback={null}>
          <QRModal
            shareUrl={getShareUrl()}
            wheelTitle={title}
            onClose={() => setShowQR(false)}
          />
        </React.Suspense>
      )}

      {/* ── Onboarding tour (first visit only) ──────────────────────────────
          Three-step spotlight walkthrough: add names → spin → customize.
          Dismissed permanently on Skip or completing step 3. */}
      {!embedMode && (
        <Suspense fallback={null}>
          <OnboardingTour
            steps={[
              {
                targetRef: sheetOpenerRef,
                title: t('entries.title'),
                body: t('tool.home.step1'),
                emoji: '📝',
              },
              {
                targetRef: spinBtnRef,
                title: t('wheel.spin'),
                body: t('tool.home.step2'),
                emoji: '🎯',
              },
              {
                targetRef: optionsBtnRef,
                title: t('wheel.options'),
                body: t('tool.home.step3'),
                emoji: '⚙️',
              },
            ]}
          />
        </Suspense>
      )}

    </>
  );
};

export default HomePage;


